import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';

import { useDispatch, useSelector } from 'react-redux';
import { NavigationService } from '../services/navigation.service';

import { UserService } from '../services/user.service';

import { RootState, saveUser, UserUmanforge, LoggerService } from '@umanforge/common';

import { Loading } from '../components/loading.component';
import { ModifyUserComponent } from 'components/users/modify_user.component';
import { TabBarComponent } from 'components/layout/tab_bar.component';
import { AbsenceUserComponent } from 'components/users/absence_user.component';
import { ActionsUserComponent } from 'components/users/actions_user.component';
import { HomeLeftBar } from 'components/menu/home_left_bar.component';
import { HomeTopBar } from 'components/menu/home_top_bar.component';
import { useTranslation } from 'react-i18next';


type UserTabMode = 'profile' | 'common:absence' | 'common:actions'


export function DetailsUserView() {
  const navigate = useNavigate();
  const params = useParams();
  const { t } = useTranslation();

  const dispatch = useDispatch();
  const token = useSelector( ( state: RootState ) => { return state.token.value; } );
  const me = useSelector( ( state: RootState ) => { return state.user.value; } );
  const logger: LoggerService = new LoggerService( 'DetailsUserView' );

  /*----------------------------- useState ------------------------------*/
  const [ user, setUser ] = useState<UserUmanforge | null>( null );

  const [ needConfirmation, setNeedConfirmation ] = useState<boolean>( false );

  const [ tabMod, setTabMod ] = useState<UserTabMode>( 'profile' );
  const [ isLoading, setIsLoading ] = useState<boolean>( false );
  const [ avatarFile, setAvatarFile ] = useState<File | null>( null );
  const [ avatarInitialUrl, setAvatarInitialUrl ] = useState<string | null>( null );
  const [ avatarChanged, setAvatarChanged ] = useState<boolean>( false );
  const [ avatarClientId, setAvatarClientId ] = useState<string>( '' );

  function computeCanSeeActions(): boolean {
    if ( !me || !user ) {return false;}
    if ( me.id === user.id ) { return true; }
    if ( me.isSuperAdmin ) {return true;}

    const targetClientIds = new Set<string>();
    const targetSiteIds = new Set<string>();
    const targetSectorIds = new Set<string>();

    for ( const client of ( user.clients ?? [] ) ) {
      if ( client && client.id ) {targetClientIds.add( client.id );}
      if ( client && client.sites ) {
        for ( const site of client.sites ) {
          if ( site && site.id ) {targetSiteIds.add( site.id );}
          if ( site && site.sectors ) {
            for ( const sector of site.sectors ) {
              if ( sector && sector.id ) {targetSectorIds.add( sector.id );}
            }
          }
        }
      }
    }

    for ( const myClient of ( me.clients ?? [] ) ) {
      if ( myClient && myClient.isAdmin && targetClientIds.has( myClient.id ) ) {return true;}
      if ( myClient && myClient.sites ) {
        for ( const mySite of myClient.sites ) {
          if ( mySite && mySite.isAdmin && targetSiteIds.has( mySite.id ) ) {return true;}
          if ( mySite && mySite.sectors ) {
            for ( const mySector of mySite.sectors ) {
              if ( mySector && mySector.isAdmin && targetSectorIds.has( mySector.id ) ) {return true;}
            }
          }
        }
      }
    }

    return false;
  }

  const canSeeActions = computeCanSeeActions();

  /*----------------------------- useEffect -----------------------------*/
  useEffect( () => {
    if ( params.userId != user?.id ) {
      refreshUser();
    }
  }, [ params ] );

  /*------------------------- Requetes API User -------------------------*/
  const refreshUser = async () => {
    setUser( null );
    setIsLoading( true );
    if ( params.userId ) {
      const refreshedUser = await UserService.getUser( params.userId, token, dispatch, () => {
        NavigationService.toUsers( navigate, false );
      } );
      if ( me != null && refreshedUser != null && me?.id === refreshedUser?.id ) {
        dispatch( saveUser( refreshedUser ) );
      }
      setUser( refreshedUser );
      // fetch avatar URL for this user
      try {
        const clientId = ( refreshedUser && refreshedUser.clients && refreshedUser.clients.length > 0 ) ? refreshedUser.clients[ 0 ].id : ( me && me.clients && me.clients[ 0 ] ? me.clients[ 0 ].id : '' );
        setAvatarClientId( clientId ?? '' );
        if ( refreshedUser && clientId ) {
          const avatarUrl = await ( await import( '../services/avatar.service' ) ).AvatarService.getUserAvatar( refreshedUser.id, clientId, token, dispatch );
          setAvatarInitialUrl( avatarUrl );
        } else {
          setAvatarInitialUrl( null );
        }
      } catch ( e ) {
        logger.error( 'Error fetching avatar url', e as Error );
        setAvatarInitialUrl( null );
      }
      // reset avatar selection state
      setAvatarFile( null );
      setAvatarChanged( false );
      setIsLoading( false );
    }
  };




  /*------------------------------- Front -------------------------------*/
  return (
    <>
      <HomeLeftBar items={ NavigationService.getNavBarItems( token, dispatch ) } needConfirmation={ needConfirmation } />
      <div className='flex flex-col min-w-[350px] h-full w-full'>
        <HomeTopBar title={ t( 'Modify user' ) } needConfirmationToNav={ needConfirmation } />
        {
          !isLoading && user ?
            <>
              <TabBarComponent
                defaultValue={ tabMod }
                onChangeMod={ key => { setTabMod( key as UserTabMode ); } }
                tabModMap={( () => {
                  const map = new Map<string, JSX.Element>();
                  map.set( 'profile', <>
                    <ModifyUserComponent
                      user={ user }
                      refreshUser={ refreshUser }
                      setNeedConfirmation={ setNeedConfirmation }
                      needConfirmation={ needConfirmation }
                      avatarFile={ avatarFile }
                      avatarChanged={ avatarChanged }
                      avatarInitialUrl={ avatarInitialUrl }
                      avatarClientId={ avatarClientId }
                      canEditAvatar={ canSeeActions }
                      onAvatarFileChange={ file => {
                        setAvatarFile( file );
                        // mark changed only when a real file is selected
                        setAvatarChanged( !!file );
                      } }
                      onAvatarInitialUrlChange={ url => {
                        setAvatarInitialUrl( url );
                      } }
                      onAvatarChanged={ changed => {
                        setAvatarChanged( changed );
                      } }
                    />
                  </> );
                  map.set( 'common:absence', <AbsenceUserComponent user={ user } refreshUser={ refreshUser } setNeedConfirmation={ setNeedConfirmation } needConfirmation={ needConfirmation }/> );
                  if ( canSeeActions ) {
                    map.set( 'common:actions', <ActionsUserComponent user={ user } refreshUser={ refreshUser } setNeedConfirmation={ setNeedConfirmation } needConfirmation={ needConfirmation }/> );
                  }
                  return map;
                } )()}
              />
            </>
            :
            <Loading/>
        }
      </div>
    </>
  );
}